import React, { useState, useEffect } from 'react';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { getAssetUrl } from '../../utils/url';

export type LoadingVariant = 'fullscreen' | 'overlay' | 'inline' | 'page';
export type LoadingAnimation = 'pulse' | 'breathing' | 'bounce' | 'spin' | 'fade';
export type LoadingMarkSize = 'xs' | 'sm' | 'md' | 'lg';

interface LoadingProps {
  /**
   * Variant tampilan loading
   * - fullscreen: Menutupi seluruh layar (untuk initial app load)
   * - overlay: Menutupi konten dengan backdrop (untuk action/submit)
   * - page: Di tengah area konten (untuk fetch data halaman)
   * - inline: Di dalam container tertentu (untuk bagian kecil)
   */
  variant?: LoadingVariant;

  /**
   * Jenis animasi
   */
  animation?: LoadingAnimation;

  /**
   * Ukuran logo
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Teks loading (opsional)
   */
  message?: string;

  /**
   * Warna background (untuk fullscreen/overlay)
   */
  backdrop?: 'light' | 'dark' | 'blur' | 'none';

  /**
   * Custom className
   */
  className?: string;

  /**
   * Force show logo meskipun belum hydrated
   */
  forceShowLogo?: boolean;
}

export interface LogoLoadingMarkProps {
  size?: LoadingMarkSize;
  className?: string;
  forceShowLogo?: boolean;
}

export interface LoadingLabelProps {
  label: string;
  size?: LoadingMarkSize;
  className?: string;
}

const useLoadingBrand = (forceShowLogo = false) => {
  const { settings, isHydrated } = useSystemSettingsStore();
  const [logoError, setLogoError] = useState(false);

  const logoUrl = getAssetUrl(settings.logo_url);
  const appName = settings.app_name || 'SIM-EVAL';
  const brandInitials = appName
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  const showLogo = (isHydrated || forceShowLogo) && logoUrl && !logoError;

  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  return {
    appName,
    brandInitials,
    logoUrl,
    showLogo,
    setLogoError,
  };
};

export const LogoLoadingMark: React.FC<LogoLoadingMarkProps> = ({
  size = 'sm',
  className = '',
  forceShowLogo = false,
}) => {
  const { appName, brandInitials, logoUrl, showLogo, setLogoError } = useLoadingBrand(forceShowLogo);

  const sizeClasses = {
    xs: 'w-4 h-4',
    sm: 'w-5 h-5',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
  };

  const fallbackTextSizes = {
    xs: 'text-[9px]',
    sm: 'text-[10px]',
    md: 'text-xs',
    lg: 'text-sm',
  };

  return (
    <span
      className={`relative inline-flex items-center justify-center shrink-0 ${sizeClasses[size]} ${className}`}
      aria-hidden="true"
    >
      {showLogo ? (
        <img
          src={logoUrl!}
          alt={`Loading ${appName}`}
          className="w-full h-full object-contain animate-breathing"
          onError={() => setLogoError(true)}
        />
      ) : (
        <span
          className={`
            w-full h-full rounded-lg bg-gradient-to-br from-teal-main to-teal-dark
            inline-flex items-center justify-center font-black text-white animate-breathing
            ${fallbackTextSizes[size]}
          `}
        >
          {brandInitials}
        </span>
      )}
    </span>
  );
};

export const LoadingLabel: React.FC<LoadingLabelProps> = ({
  label,
  size = 'xs',
  className = '',
}) => (
  <span className={`inline-flex items-center gap-2 ${className}`}>
    <LogoLoadingMark size={size} />
    <span>{label}</span>
  </span>
);

const Loading: React.FC<LoadingProps> = ({
  variant = 'inline',
  animation = 'breathing',
  size = 'md',
  message,
  backdrop = 'blur',
  className = '',
  forceShowLogo = false,
}) => {
  const { appName, brandInitials, logoUrl, showLogo, setLogoError } = useLoadingBrand(forceShowLogo);

  // Size mapping
  const sizeClasses = {
    sm: 'w-12 h-12',
    md: 'w-20 h-20',
    lg: 'w-32 h-32',
    xl: 'w-48 h-48',
  };

  // Animation classes
  const animationClasses = {
    pulse: 'animate-pulse',
    breathing: 'animate-breathing',
    bounce: 'animate-bounce',
    spin: 'animate-spin',
    fade: 'animate-fade',
  };

  // Backdrop classes
  const backdropClasses = {
    light: 'bg-white/90',
    dark: 'bg-neutral-900/90',
    blur: 'bg-white/70 backdrop-blur-md',
    none: '',
  };

  // Container berdasarkan variant
  const containerClasses = {
    fullscreen: `fixed inset-0 z-[9999] flex flex-col items-center justify-center ${backdropClasses[backdrop]}`,
    overlay: `fixed inset-0 z-50 flex flex-col items-center justify-center ${backdropClasses[backdrop]}`,
    page: `flex flex-col items-center justify-center min-h-[400px] py-12`,
    inline: `flex flex-col items-center justify-center py-8`,
  };

  return (
    <div className={`${containerClasses[variant]} ${className}`}>
      {/* Logo Container */}
      <div className={`relative ${sizeClasses[size]}`}>
        {showLogo ? (
          // Logo Image dengan animasi
          <div className={`w-full h-full ${animationClasses[animation]}`}>
            <img
              src={logoUrl!}
              alt={`Loading ${appName}`}
              className="w-full h-full object-contain"
              onError={() => setLogoError(true)}
            />
          </div>
        ) : (
          // Fallback: Initials dengan gradient background
          <div
            className={`
              w-full h-full rounded-2xl
              bg-gradient-to-br from-teal-main to-teal-dark
              flex items-center justify-center
              text-white font-black
              ${animationClasses[animation]}
            `}
            style={{
              fontSize: size === 'sm' ? '1rem' : size === 'md' ? '1.5rem' : size === 'lg' ? '2.5rem' : '4rem',
            }}
          >
            {brandInitials}
          </div>
        )}

        {/* Spinner overlay (opsional, hanya untuk variant tertentu) */}
        {animation === 'spin' && variant === 'fullscreen' && (
          <div className="absolute inset-0 rounded-full border-4 border-teal-main/20 border-t-teal-main animate-spin" />
        )}
      </div>

      {/* Loading Message */}
      {message && (
        <p
          className={`
            mt-4 font-medium text-center
            ${variant === 'fullscreen' || variant === 'overlay' ? 'text-neutral-text-primary' : 'text-neutral-text-secondary'}
            ${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-base'}
          `}
        >
          {message}
        </p>
      )}

      {/* Animated dots (untuk fullscreen/overlay) */}
      {(variant === 'fullscreen' || variant === 'overlay') && !message && (
        <div className="mt-4 flex items-center gap-1">
          <div className="w-1.5 h-1.5 bg-teal-main rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-1.5 h-1.5 bg-teal-main rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-1.5 h-1.5 bg-teal-main rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      )}
    </div>
  );
};

export default Loading;
